/*
 * Copyright 2020 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {
  createApiFactory,
  createPlugin,
  createRoutableExtension,
  discoveryApiRef,
  fetchApiRef,
  identityApiRef,
} from '@mainstage/core-plugin-api';
import { scmIntegrationsApiRef } from '@mainstage/integration-react';
import { createScaffolderFieldExtension } from '@mainstage/plugin-scaffolder-react';
import { scaffolderApiRef, ScaffolderClient } from './api';
import { CronExpressionPicker } from './components/fields/CronExpressionPicker/CronExpressionPicker';
import { entityNamePickerValidation } from './components/fields/EntityNamePicker';
import { EntityNamePicker } from './components/fields/EntityNamePicker/EntityNamePicker';
import { EntityPicker } from './components/fields/EntityPicker/EntityPicker';
import { EntitySearchPicker } from './components/fields/EntitySearchPicker/EntitySearchPicker';
import { EntityTagsPicker } from './components/fields/EntityTagsPicker/EntityTagsPicker';
import { OwnedEntityPicker } from './components/fields/OwnedEntityPicker/OwnedEntityPicker';
import { OwnerPicker } from './components/fields/OwnerPicker/OwnerPicker';
import { repoPickerValidation } from './components/fields/RepoUrlPicker';
import { RepoUrlPicker } from './components/fields/RepoUrlPicker/RepoUrlPicker';
import {
  blueprintRouteRef,
  nextRouteRef,
  registerComponentRouteRef,
  rootRouteRef,
  templateRouteRef,
  viewTechDocRouteRef,
  templateIndexRouteRef,
  actionsRouteRef,
  templateEditorRouteRef,
  taskListRouteRef,
} from './routes';

/**
 * The main plugin export for the scaffolder.
 * @public
 */
export const scaffolderPlugin = createPlugin({
  id: 'scaffolder',
  apis: [
    createApiFactory({
      api: scaffolderApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        scmIntegrationsApi: scmIntegrationsApiRef,
        fetchApi: fetchApiRef,
        identityApi: identityApiRef,
      },
      factory: ({ discoveryApi, scmIntegrationsApi, fetchApi, identityApi }) =>
        new ScaffolderClient({
          discoveryApi,
          scmIntegrationsApi,
          fetchApi,
          identityApi,
        }),
    }),
  ],
  routes: {
    root: rootRouteRef,
    blueprintRouteRef: blueprintRouteRef,
    templateRouteRef: templateRouteRef,
  },
  externalRoutes: {
    registerComponent: registerComponentRouteRef,
    viewTechDoc: viewTechDocRouteRef,
    templateIndexRouteRef: templateIndexRouteRef,
    actionsRouteRef: actionsRouteRef,
    templateEditorRouteRef: templateEditorRouteRef,
    taskListRouteRef: taskListRouteRef,
  },
});

/**
 * A field extension for selecting an Entity that exists in the Catalog.
 *
 * @public
 */
export const EntityPickerFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    component: EntityPicker,
    name: 'EntityPicker',
  }),
);

/**
 * A field extension for selecting an Entity that exists in the Catalog.
 *
 * @public
 */
export const CronExpressionPickerFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    component: CronExpressionPicker,
    name: 'CronExpressionPicker',
  }),
);

/**
 * A field extension for selecting System, Domain, ConsumableInterface or Resource.
 *
 * @public
 */
export const EntitySearchPickerFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    component: EntitySearchPicker,
    name: 'EntitySearchPicker',
  }),
);

/**
 * The field extension for selecting a name for a new Entity in the Catalog.
 *
 * @public
 */
export const EntityNamePickerFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    component: EntityNamePicker,
    name: 'EntityNamePicker',
    validation: entityNamePickerValidation,
  }),
);

/**
 * The field extension which provides the ability to select a RepositoryUrl.
 * Currently this is an encoded URL that looks something like the following `github.com?repo=myRepoName&owner=backstage`.
 *
 * @public
 */
export const RepoUrlPickerFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    component: RepoUrlPicker,
    name: 'RepoUrlPicker',
    validation: repoPickerValidation,
  }),
);

/**
 * A field extension for picking users and groups out of the Catalog.
 *
 * @public
 */
export const OwnerPickerFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    component: OwnerPicker,
    name: 'OwnerPicker',
  }),
);

/**
 * The Router and main entrypoint to the Scaffolder plugin.
 *
 * @public
 */
export const ScaffolderPage = scaffolderPlugin.provide(
  createRoutableExtension({
    name: 'ScaffolderPage',
    component: () =>
      import('./components/Router').then(m => m.ScaffolderRouter),
    mountPoint: rootRouteRef,
  }),
);

/**
 * The Router and main entrypoint to the Templates page.
 *
 * @public
 */
export const TemplatesPage = scaffolderPlugin.provide(
  createRoutableExtension({
    name: 'TemplatesPage',
    component: () => import('./components/Router').then(m => m.TemplatesRouter),
    mountPoint: templateRouteRef,
  }),
);

/**
 * The Router and main entrypoint to the Blueprints page.
 *
 * @public
 */
export const BlueprintsPage = scaffolderPlugin.provide(
  createRoutableExtension({
    name: 'BlueprintsPage',
    component: () =>
      import('./components/Router').then(m => m.BlueprintsRouter),
    mountPoint: blueprintRouteRef,
  }),
);

/**
 * A field extension to show all the Entities that are owned by the current logged-in User for use in templates.
 *
 * @public
 */
export const OwnedEntityPickerFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    component: OwnedEntityPicker,
    name: 'OwnedEntityPicker',
  }),
);

/**
 * EntityTagsPickerFieldExtension
 * @public
 */
export const EntityTagsPickerFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    component: EntityTagsPicker,
    name: 'EntityTagsPicker',
  }),
);

/**
 * @alpha
 * The Router and main entrypoint to the Alpha Scaffolder plugin.
 */
export const NextScaffolderPage = scaffolderPlugin.provide(
  createRoutableExtension({
    name: 'NextScaffolderPage',
    component: () => import('./next/Router').then(m => m.Router),
    mountPoint: nextRouteRef,
  }),
);
